import { useClickOutSideWithState } from "@/hooks";
import styles from "@/overlay/components/QuickAccessLabel/LabelTitle.module.scss";
import { convertToCSSProperties } from "@/utility/style";
import type { ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { useEffect, useRef, useState } from "react";

export interface LabelTitleProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  titleStyle?: ElementStyle;
  onTitleClick: () => void;
  disabled?: boolean;
}

export function LabelTitle({ title, onTitleClick, onTitleChange, titleStyle, disabled }: LabelTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title || "");

  const isTitleBlank = editableTitle.trim().length === 0;

  const singleClickTimerRef = useRef<number | null>(null);

  const handleSubmit = () => {
    setIsEditing(false);
    if (onTitleChange) {
      onTitleChange(editableTitle);
    }
  };

  const { setDirty, nodeRef } = useClickOutSideWithState({
    onSubmit: handleSubmit,
    isDirty: isEditing,
  });

  useEffect(() => {
    setEditableTitle(title || "");
  }, [title]);

  useEffect(() => {
    if (isEditing && nodeRef.current) {
      const spanElement = document.getElementById("editable-title-span");
      if (spanElement) {
        nodeRef.current.style.width = `${spanElement.offsetWidth}px`;
      }
    }
  }, [isEditing, nodeRef]);

  const handleTitleDoubleClick = () => {
    if (singleClickTimerRef.current !== null) {
      window.clearTimeout(singleClickTimerRef.current);
      singleClickTimerRef.current = null;
    }
    setIsEditing(true);
    setDirty(true);
  };

  const handleContainerClick = () => {
    if (singleClickTimerRef.current !== null) {
      window.clearTimeout(singleClickTimerRef.current);
    }
    singleClickTimerRef.current = window.setTimeout(() => {
      onTitleClick();
      singleClickTimerRef.current = null;
    }, 200);
  };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTitle(event.target.value);
    setDirty(true);
  };

  const handleTitleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };
  const handleIconClick: React.MouseEventHandler<HTMLSpanElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleTitleDoubleClick();
  };

  const handleIconKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      handleTitleDoubleClick();
    }
  };

  return (
    <>
      {!isEditing ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <div
          className={`${styles["label-title"]} ${isTitleBlank ? styles["label-title--empty"] : ""}`}
          onClick={handleContainerClick}
          onDoubleClick={handleTitleDoubleClick} // Trigger edit on double-click
          data-testid="label-title"
          style={{
            ...(disabled ? { pointerEvents: "none" } : {}),
            ["--lt-icon-size" as any]: titleStyle?.fontSize ? `${titleStyle.fontSize}px` : "1rem",
          }}
        >
          <span
            className={`${styles["on-hover-icon"]} ${styles["on-hover-icon--clickable"]} material-icons-outlined grid-arrangement-icon`}
            style={{
              fontSize: titleStyle?.fontSize || "1rem",
              textDecoration: "none !important",
            }}
            role="button"
            tabIndex={0}
            aria-label="Edit title"
            onClick={handleIconClick}
            onKeyDown={handleIconKeyDown}
          >
            edit
          </span>
          <span
            className={styles["label-text"]}
            style={{
              ...convertToCSSProperties(titleStyle),
            }}
          >
            {editableTitle.replace(/ /g, "\u00a0")}
          </span>
        </div>
      ) : (
        <input
          ref={nodeRef}
          type="text"
          // biome-ignore lint/a11y/noAutofocus: <explanation>
          autoFocus={true}
          value={editableTitle}
          onChange={handleTitleChange}
          onKeyDown={handleTitleKeyPress}
          className={styles["label-title-input"]}
          style={{ fontSize: titleStyle?.fontSize || "1rem" }}
          data-testid="label-title-input"
        />
      )}
    </>
  );
}
